<template>
  <textarea
    v-if="multiline"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readOnly="readOnly"
    :type="type"
    :rows="rows"
    :class="classes"
    @input="
      $emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)
    "
    @blur="$emit('blur')"
  ></textarea>

  <input
    v-else
    ref="input"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readOnly="readOnly"
    :type="type"
    :class="classes"
    @input="
      $emit('update:modelValue', ($event.target as HTMLInputElement).value)
    "
    @blur="$emit('blur')"
  />
</template>

<script lang="ts" setup>
type Props = {
  modelValue?: string
  placeholder?: string
  type?: string
  disabled?: boolean
  readOnly?: boolean
  multiline?: boolean
  rows?: number
  fullWidth?: boolean
  autoFocus?: boolean
  error?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: undefined,
  type: 'text',
  rows: undefined,
})

defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'blur'): void
}>()

const classes = computed(() => ({
  'app-text-field': true,
  'app-text-field--error': props.error,
  'app-text-field--full-width': props.fullWidth,
}))

const input = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

onMounted(() => {
  nextTick(() => {
    if (props.autoFocus) input.value?.focus()
  })
})
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-text-field {
  display: inline-block;
  padding: 0.8125rem 0.75rem;
  font-family: $font-barlow;
  font-size: 0.875rem;
  line-height: 1.42857143;
  color: $black;
  background-color: $gray-100;
  border: 1px solid $gray-200;
  border-radius: 0.125rem;
  @include transition('color, background-color, border-color');

  &::placeholder {
    color: $gray-400;
  }

  &:hover {
    border-color: $gray-300;
  }

  &:focus {
    background-color: $white;
    border-color: $black;
    outline: none;
  }

  &.app-text-field--error {
    background-color: $white;
    border-color: $signal-error-red;
  }

  @include media-breakpoint-up(md) {
    padding: 1rem;
    font-size: 1rem;
    line-height: 1.25;
  }
}

.app-text-field--full-width {
  display: block;
  width: 100%;
}
</style>
